import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import { alpha, useTheme } from '@mui/material/styles'

import { PageMeta } from '../../components/common/PageMeta'
import { PhotoStrip } from '../../components/content/PhotoStrip'
import { CardSkeletonGrid, ErrorState, Section } from '../../components/common/States'
import { SectionChips } from '../../components/layout/SectionChips'
import { GuestReviews } from '../../components/reviews/GuestReviews'
import { LocationMap } from '../../components/location/LocationMap'
import { UnitCard } from '../../components/units/UnitCard'
import { ActivityCard } from '../activities/ActivityCard'
import { contentApi, unitsApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { seasonColors } from '../../theme/theme'

export function HomePage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const theme = useTheme()

  const units = useQuery({ queryKey: ['units', locale], queryFn: () => unitsApi.list(locale) })
  const season = useQuery({ queryKey: ['currentSeason'], queryFn: contentApi.currentSeason })
  const activities = useQuery({
    queryKey: ['activities', locale, season.data],
    queryFn: () => contentApi.activities(locale, season.data),
    enabled: Boolean(season.data),
  })
  const destinations = useQuery({
    queryKey: ['destinations', locale],
    queryFn: () => contentApi.destinations(locale),
  })

  const currentSeason = season.data ?? 'SUMMER'

  return (
    <>
      <PageMeta
        title={t('home.heroTitle')}
        description={t('home.heroSubtitle')}
        path=""
      />

      {/* ---------------------------------------------------------------- hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'primary.dark',
          color: 'common.white',
          // A layered gradient stands in for the photograph until the owner supplies one.
          // It reads as water and sky rather than as a missing image.
          backgroundImage: `
            radial-gradient(120% 90% at 78% 8%, ${alpha(theme.palette.primary.light, 0.55)} 0%, transparent 60%),
            radial-gradient(90% 70% at 12% 92%, ${alpha('#0A3B42', 0.9)} 0%, transparent 62%),
            linear-gradient(160deg, ${theme.palette.primary.dark} 0%, #072B31 100%)
          `,
        }}
      >
        <Container sx={{ py: { xs: 9, md: 15 }, position: 'relative' }}>
          <Stack spacing={3} sx={{ maxWidth: 720 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: '0.16em' }}>
              {t('home.heroKicker')}
            </Typography>

            <Typography variant="h1" component="h1">
              {t('home.heroTitle')}
            </Typography>

            <Typography variant="subtitle1" sx={{ color: alpha('#FFFFFF', 0.86), fontSize: '1.15rem' }}>
              {t('home.heroSubtitle')}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                component={RouterLink}
                to="/houses"
                variant="contained"
                size="large"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
              >
                {t('home.heroCta')}
              </Button>
              <Button
                component={RouterLink}
                to="/activities"
                variant="outlined"
                size="large"
                sx={{
                  color: 'common.white',
                  borderColor: alpha('#FFFFFF', 0.5),
                  '&:hover': { borderColor: 'common.white', bgcolor: alpha('#FFFFFF', 0.08) },
                }}
              >
                {t('activities.title')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/*
        The chip rail sits below the hero, not above it: the hero is what the page opens with,
        and the chips become useful the moment you start scrolling past it - which is exactly
        when they stick under the header.
      */}
      <SectionChips
        marks={[
          { id: 'why', emoji: '\u{1F332}', label: t('home.chipWhy') },
          { id: 'houses', emoji: '\u{1F3E1}', label: t('nav.houses') },
          { id: 'reviews', emoji: '\u{2B50}', label: t('home.chipReviews') },
          { id: 'activities', emoji: '\u{1F6F6}', label: t('nav.activities') },
          { id: 'pictures', emoji: '\u{1F4F7}', label: t('nav.gallery') },
          { id: 'explore', emoji: '\u{1F9ED}', label: t('nav.aroundHere') },
          { id: 'find-us', emoji: '\u{1F4CD}', label: t('home.chipFindUs') },
        ]}
      />

      {/* ---------------------------------------------------------------- why */}
      <Section id="why" title={t('home.highlightsTitle')}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          {[
            { icon: '🧖', title: t('home.highlightSauna'), body: t('home.highlightSaunaBody') },
            { icon: '🤫', title: t('home.highlightQuiet'), body: t('home.highlightQuietBody') },
            { icon: '🎭', title: t('home.highlightOpera'), body: t('home.highlightOperaBody') },
          ].map((item) => (
            <Card key={item.title} sx={{ bgcolor: 'transparent', border: 'none' }}>
              <CardContent sx={{ px: 0 }}>
                <Box aria-hidden sx={{ fontSize: '2.25rem', mb: 1.5, lineHeight: 1 }}>
                  {item.icon}
                </Box>
                <Typography variant="h4" component="h3" sx={{ mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.body}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Section>

      {/* ---------------------------------------------------------------- houses */}
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Section
          id="houses"
          title={t('home.housesTitle')}
          subtitle={t('home.housesSubtitle')}
          action={
            <Button component={RouterLink} to="/houses" endIcon={<ArrowForwardIcon />}>
              {t('common.seeMore')}
            </Button>
          }
        >
          {units.isPending && <CardSkeletonGrid count={3} />}
          {units.isError && <ErrorState error={units.error} onRetry={() => void units.refetch()} />}
          {units.data && (
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              }}
            >
              {units.data.slice(0, 3).map((unit) => (
                <UnitCard key={unit.id} unit={unit} />
              ))}
            </Box>
          )}
        </Section>
      </Box>

      {/* ---------------------------------------------------------------- reviews */}
      {/* Straight after the houses: the reviews are evidence for the thing just shown. */}
      <Section id="reviews" title={t('about.whatGuestsSay')} subtitle={t('about.whatGuestsSayBody')}>
        <GuestReviews heading={false} />
      </Section>

      {/* ---------------------------------------------------------------- this season */}
      <Section
        id="activities"
        kicker={t('activities.seasonNow', { season: t(`activities.${currentSeason}`) })}
        title={t('home.activitiesTitle')}
        subtitle={t('home.activitiesSubtitle')}
        action={
          <Button component={RouterLink} to="/activities" endIcon={<ArrowForwardIcon />}>
            {t('common.seeMore')}
          </Button>
        }
      >
        <Chip
          label={t(`activities.${currentSeason}`)}
          sx={{
            mb: 3,
            bgcolor: alpha(seasonColors[currentSeason], 0.14),
            color: seasonColors[currentSeason],
            fontWeight: 700,
          }}
        />

        {activities.isPending && <CardSkeletonGrid count={3} height={280} />}
        {activities.isError && (
          <ErrorState error={activities.error} onRetry={() => void activities.refetch()} />
        )}
        {activities.data && (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            }}
          >
            {activities.data.slice(0, 3).map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          </Box>
        )}
      </Section>

      {/* ---------------------------------------------------------------- the place itself */}
      <Section id="pictures" title={t('home.picturesTitle')} subtitle={t('home.picturesSubtitle')}>
        <Stack spacing={4}>
          <PhotoStrip category="NATURE" count={6} showAllLink={false} />
          <PhotoStrip category="HOUSE" count={6} showAllLink={false} />
          <PhotoStrip kind="VIDEO" count={6} title={t('gallery.films')} showAllLink={false} />
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/gallery"
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              {t('gallery.seeAll')}
            </Button>
          </Box>
        </Stack>
      </Section>

      {/* ---------------------------------------------------------------- explore */}
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Section
          id="explore"
          title={t('home.exploreTitle')}
          subtitle={t('home.exploreSubtitle')}
          action={
            <Button component={RouterLink} to="/explore" endIcon={<ArrowForwardIcon />}>
              {t('common.seeMore')}
            </Button>
          }
        >
          {destinations.isPending && <CardSkeletonGrid count={4} height={200} />}
          {destinations.data && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              }}
            >
              {destinations.data.map((destination) => (
                <Card
                  key={destination.id}
                  component={RouterLink}
                  to={`/explore/${destination.slug}`}
                  sx={{
                    textDecoration: 'none',
                    p: 2.5,
                    display: 'block',
                    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                  }}
                >
                  <Typography variant="h5" component="h3" color="text.primary" sx={{ mb: 0.5 }}>
                    {destination.name}
                  </Typography>
                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                    {destination.distanceKm != null
                      ? t('explore.kmAway', { count: destination.distanceKm })
                      : destination.region}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {destination.summary}
                  </Typography>
                </Card>
              ))}
            </Box>
          )}
        </Section>
      </Box>

      {/* ---------------------------------------------------------------- find us */}
      <Section id="find-us" title={t('home.findUsTitle')} subtitle={t('home.findUsSubtitle')}>
        <LocationMap />
      </Section>
    </>
  )
}
